import { PageHeader } from "@/components/ui/design-system";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { UputaZaInstalaciju } from "@/components/UputaZaInstalaciju";
import { buttonVariants } from "@/components/ui/button";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { datum } from "@/lib/format";
import { cn } from "@/lib/utils";
import { offlineUnosDostupan } from "@/lib/instalacija";
import { dohvatiRadnike, punoIme, type Radnik } from "@/lib/radnici";
import {
  dohvatiRupe,
  dohvatiSazetak,
  opisRupe,
  type Rupa,
  type Sazetak,
} from "@/lib/sazetak";

/**
 * The site manager's home screen — variant B of the designs, at 380px.
 *
 * One job per screen: get the hours in. Everything else is a consequence of
 * that — who may not be on site today, and how much of today is already
 * recorded. Controls are 52px because this is used outdoors, in gloves, on a
 * phone held in one hand.
 *
 * Until the roster exists (O-13), this person reaches no workers. The screen
 * says so plainly rather than showing zeros that look like an answer: zero
 * workers and "no roster yet" are different facts, and only one of them is
 * true.
 */

export function PocetnaVoditeljPage() {
  const { user } = useAuth();
  const [radnici, setRadnici] = useState<Radnik[] | null>(null);
  const [sazetak, setSazetak] = useState<Sazetak | null>(null);
  const [rupe, setRupe] = useState<Rupa[]>([]);

  useEffect(() => {
    const kontroler = new AbortController();
    dohvatiRadnike({ limit: 200, samo_aktivni: true }, kontroler.signal)
      .then((odgovor) => setRadnici(odgovor.items))
      .catch(() => setRadnici([]));

    // Counted on the server, over the roster (Z-13). This screen used to
    // render a hardcoded zero next to a real total, so it said "0 od 3" on a
    // day whose hours were all recorded — and would have said it for ever.
    dohvatiSazetak(undefined, kontroler.signal)
      .then(setSazetak)
      .catch(() => setSazetak(null));

    // Days that never arrived, from three days back (O-18). A nicety in the
    // sense that the rest of the screen works without it — but the one thing
    // here the manager cannot find out from his own phone.
    dohvatiRupe(undefined, kontroler.signal)
      .then(setRupe)
      .catch(() => setRupe([]));

    return () => kontroler.abort();
  }, []);

  if (!radnici) {
    return <SkeletonTablica redaka={2} />;
  }

  const bezRasporeda = radnici.length === 0;
  const blokirani = radnici.filter(
    (radnik) =>
      radnik.dozvola_status?.kriticno ||
      radnik.zdravstveni_status?.smije_raditi === false,
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={<>Dobro jutro{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}</>} meta={<></>} />

      {/* On an iPhone still in a Safari tab the install steps take the place
          of the entry control entirely (§2.9) - offering entry that iOS will
          quietly discard is worse than not offering it. */}
      {!offlineUnosDostupan() ? (
        <UputaZaInstalaciju />
      ) : (
        /* The one yellow button on the screen, and now it goes somewhere. */
        <Link
          to="/sati"
          className={buttonVariants({ className: "h-[52px] w-full" })}
        >
          Unesi sate
        </Link>
      )}

      <section className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <p className="oznaka">Danas potvrđeno</p>
        {bezRasporeda ? (
          <p className="mt-2 text-[var(--color-muted-foreground)]">
            Raspored još nije postavljen. Kad kadrovska rasporedi radnike na
            vaše gradilište, ovdje će pisati koliko ih je danas potvrđeno.
          </p>
        ) : (
          <p className="broj mt-2 text-[var(--text-title)] font-bold">
            {sazetak
              ? `${sazetak.evidentirano} od ${sazetak.na_rasporedu}`
              : `— od ${radnici.length}`}
          </p>
        )}
      </section>

      {/* O-18. The device cannot report its own silence: a lost phone, an app
          never installed and a queue that never drained all look the same from
          here. The server compares the roster with what arrived and says which
          day is missing, from the third day on. */}
      {rupe.length > 0 ? (
        <section
          className={cn(
            "rounded-[var(--radius-card)] border p-6",
            rupe.some((rupa) => rupa.prekrsaj)
              ? "border-[var(--color-status-bad-fg)]/25 bg-[var(--color-status-bad-bg)]"
              : "border-[var(--color-status-warn-fg)]/30 bg-[var(--color-status-warn-bg)]",
          )}
        >
          <p className="font-semibold">
            {rupe.length === 1 ? "Nedostaje evidencija" : "Nedostaje evidencija za više dana"}
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {rupe.map((rupa) => (
              <li
                key={`${rupa.gradiliste_id}-${rupa.datum}`}
                className="flex items-center justify-between gap-3"
              >
                <span>
                  {opisRupe(rupa)}
                  {rupa.bez_evidencije < rupa.na_rasporedu ? (
                    <span className="text-[var(--color-muted-foreground)]">
                      {" "}
                      — <span className="broj">{rupa.bez_evidencije}</span> od{" "}
                      <span className="broj">{rupa.na_rasporedu}</span> radnika
                    </span>
                  ) : null}
                </span>
                <Link
                  to={`/sati?datum=${rupa.datum}`}
                  className="shrink-0 font-medium underline underline-offset-2"
                >
                  Unesi
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {blokirani.length > 0 ? (
        <section className="rounded-[var(--radius-card)] border border-[var(--color-status-bad-fg)]/25 bg-[var(--color-status-bad-bg)] p-6">
          <p className="text-[var(--color-status-bad-fg)] font-semibold">
            <span className="broj">{blokirani.length}</span>{" "}
            {blokirani.length === 1 ? "radnik ne smije" : "radnika ne smije"} na
            gradilište
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {blokirani.map((radnik) => (
              <li key={radnik.id} className="flex items-center justify-between gap-3">
                <span>{punoIme(radnik)}</span>
                <Link
                  to={`/zaposlenici/${radnik.id}`}
                  className="font-medium underline underline-offset-2"
                >
                  Otvori
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {!bezRasporeda ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-[var(--text-card)] font-semibold">
            Radnici na gradilištu
          </h2>
          <ul className="flex flex-col gap-2">
            {radnici.map((radnik) => (
              <li
                key={radnik.id}
                className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4"
              >
                <p className="font-medium">{punoIme(radnik)}</p>
                <p className="text-[var(--color-muted-foreground)]">
                  {radnik.radno_mjesto ?? "—"}
                </p>
                {/* The single health line a site manager gets: may work, and
                    until when. No verdict, no dates of examinations, no
                    reason (§7). */}
                <p className="mt-1">
                  {radnik.zdravstveni_status
                    ? radnik.zdravstveni_status.smije_raditi
                      ? radnik.zdravstveni_status.vrijedi_do
                        ? `Smije raditi do ${datum(radnik.zdravstveni_status.vrijedi_do)}`
                        : "Smije raditi"
                      : "Ne smije raditi"
                    : "Liječnički nije predan"}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
